/**
 * Sign-in and the session boundary.
 *
 * One page for everyone: staff go on to Cloudflare Zero Trust, clients sign in
 * with the account they made in the widget (`/c/auth/*`).
 *
 * Staff have no password and no sign-up. Cloudflare Zero Trust authenticates
 * against an approved domain before the request reaches the Worker, and the
 * Worker exchanges that for a Supabase session. This page exists so the first
 * thing anyone sees is ours rather than Cloudflare's — which is also why `/`
 * is deliberately outside the Access application while `/api/*` is inside it.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';

import { useNavigate } from 'react-router-dom';

import { clientApi, type ClientSession, type PendingSession } from '../client/client-api.js';
import {
  accessSignInUrl,
  completeAccessSignIn,
  isConfigured,
  staffPasswordSignIn,
  staffSignInOptions,
  supabase,
  type StaffSignIn,
} from '../lib/supabase.js';

export type AuthState =
  | { readonly status: 'loading' }
  | { readonly status: 'signed_out' }
  | { readonly status: 'signed_in'; readonly session: Session };

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    if (!isConfigured()) {
      setState({ status: 'signed_out' });
      return;
    }

    let active = true;

    // The redirect back from Access lands here with a one-time token in the
    // fragment. It has to be exchanged before `getSession`, or the first paint
    // is the sign-in page for someone who has just signed in.
    void (async () => {
      try {
        await completeAccessSignIn();
      } catch {
        // A spent or expired token is not worth a screen of its own: the
        // sign-in page below is the correct place to end up, and the button
        // there starts the trip again.
      }
      const { data } = await supabase().auth.getSession();
      if (!active) return;
      setState(
        data.session === null
          ? { status: 'signed_out' }
          : { status: 'signed_in', session: data.session },
      );
    })();

    // The listener covers the OAuth redirect landing and, more importantly,
    // token refresh: without it a long-lived tab keeps using an expired token
    // and every request starts failing with a 401 the user cannot explain.
    const { data: subscription } = supabase().auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setState(session === null ? { status: 'signed_out' } : { status: 'signed_in', session });
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return state;
}

/**
 * The Relay mark.
 *
 * Three nodes and the hops between them: captured, triaged, handed on. Inline
 * rather than a file because it is eleven elements and a request for it would
 * cost more than the bytes.
 *
 * Exported because the sidebar wants the same one. A product with two logos
 * has none.
 */
export function RelayMark({ size = 18 }: { readonly size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* The hand-off, drawn: caught low, lifted, passed on. Legible at 18px,
          which a row of equal dots was not. */}
      <path
        d="M4 17 L12 7 L20 12"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.45"
      />
      <circle cx="4" cy="17" r="2.6" fill="currentColor" opacity="0.55" />
      <circle cx="12" cy="7" r="2.6" fill="currentColor" opacity="0.8" />
      <circle cx="20" cy="12" r="2.6" fill="currentColor" />
    </svg>
  );
}

/** Cloudflare's mark, for the button that hands over to Zero Trust. */
function CloudflareMark(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16.5 17H7.2a3.7 3.7 0 0 1-.5-7.36 5 5 0 0 1 9.4-1.3A3.4 3.4 0 0 1 21 11.7a3.3 3.3 0 0 1-3.2 5.3Z"
        fill="#F6821F"
      />
      <path d="M16.5 17H7.2a3.7 3.7 0 0 1-.5-7.36 5 5 0 0 1 9.4-1.3" stroke="#FBAD41" strokeWidth="0.9" fill="none" />
    </svg>
  );
}

/** The minimum that makes a password worth having. Matches the server and the widget. */
const MIN_PASSWORD = 10;

const STEPS: readonly (readonly [string, string])[] = [
  ['01', 'A client points at what is wrong, in their own words.'],
  ['02', 'The page, the browser, the console errors and the failed requests come with it.'],
  ['03', 'You triage it here, and it becomes work somebody can start.'],
];

/**
 * The one sign-in page, for staff and clients alike.
 *
 * Email first, because that is the one thing everybody knows about
 * themselves and the one thing that says which way they sign in. A staff
 * domain goes on to Cloudflare Zero Trust, which stays the only gate on the
 * staff API; any other address is asked for its password, and for the emailed
 * code the first time. `/c/auth/method` decides: by domain outside our own,
 * so a client domain never reveals whether an account exists; by account on
 * our own domains, so a real client account there is asked for its password
 * instead of being sent to Zero Trust.
 *
 * Staff whose own address is outside the allowlist — an invited contractor —
 * are told "client" by that check, so the Zero Trust button is always on the
 * page as well. Locally, where Access is not configured, staff get a password
 * box instead; the Worker only offers it in development.
 */
export function SignIn(props: {
  /** Where a signed-in client goes. Defaults to the client portal. */
  readonly onClientSignedIn?: (session: ClientSession) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffSignIn | null>(null);
  const [step, setStep] = useState<'email' | 'password' | 'code' | 'staff-password' | 'reset' | 'new-password'>('email');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pending, setPending] = useState<PendingSession | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [codeFor, setCodeFor] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void staffSignInOptions().then((options) => {
      if (active) setStaff(options);
    });
    return () => {
      active = false;
    };
  }, []);

  const { onClientSignedIn } = props;
  const clientSignedIn = useCallback(
    (session: ClientSession) => {
      if (onClientSignedIn !== undefined) onClientSignedIn(session);
      else void navigate('/client');
    },
    [navigate, onClientSignedIn],
  );

  const goToAccess = useCallback(() => {
    setBusy(true);
    // A navigation, not a fetch. Access answers an unauthenticated request
    // with a redirect to its own login, and that is a page the person has to
    // actually see and use.
    window.location.assign(accessSignInUrl(`${window.location.pathname}${window.location.search}`));
  }, []);

  const fail = (caught: unknown, fallback: string): void => {
    setError(caught instanceof Error ? caught.message : fallback);
  };

  const startOver = useCallback(() => {
    setStep('email');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPending(null);
    setCode('');
    setCodeFor(null);
    setError(null);
    setNotice(null);
  }, []);

  const continueWithEmail = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const method = await clientApi.method(email.trim());
      if (method === 'client') {
        setStep('password');
      } else if (staff?.devPassword === true) {
        setStep('staff-password');
      } else if (staff?.access === 'unconfigured') {
        setError('This deployment is not behind Cloudflare Access yet, so staff cannot sign in. See docs/deployment.md.');
      } else {
        goToAccess();
        return;
      }
    } catch (caught) {
      fail(caught, 'We could not check that address.');
    }
    setBusy(false);
  }, [email, goToAccess, staff]);

  /** True when a code was sent, so "send a new code" knows whether to say so. */
  const clientSignIn = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await clientApi.signIn(email.trim(), password);
      if (outcome.kind === 'signed-in') {
        clientSignedIn(outcome.session);
        return false;
      }
      setCodeFor(outcome.email);
      setCode('');
      setNotice(null);
      setStep('code');
      return true;
    } catch (caught) {
      fail(caught, 'We could not sign you in.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [clientSignedIn, email, password]);

  const digits = code.replace(/\s+/g, '');
  const codeReady = /^\d{6,10}$/.test(digits);

  const verify = useCallback(async () => {
    if (codeFor === null || !codeReady) return;
    setBusy(true);
    setError(null);
    try {
      clientSignedIn(await clientApi.verify(codeFor, digits));
    } catch (caught) {
      fail(caught, 'That code did not work.');
    } finally {
      setBusy(false);
    }
  }, [clientSignedIn, codeFor, codeReady, digits]);

  /** Forgotten password: send a code, then take the code and a new password. */
  const startReset = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await clientApi.resetStart(email.trim());
      setCode('');
      setNewPassword('');
      setStep('reset');
      setNotice(null);
    } catch (caught) {
      fail(caught, 'We could not send a code just now.');
    } finally {
      setBusy(false);
    }
  }, [email]);

  const resetCodeReady = /^\d{6,10}$/.test(code.replace(/\s+/g, ''));
  const passwordsMatch = newPassword === confirmPassword;
  const newPasswordReady = newPassword.length >= MIN_PASSWORD && passwordsMatch;

  /** The code on its own, so a typo is caught before a password is chosen. */
  const checkResetCode = useCallback(async () => {
    if (!resetCodeReady) return;
    setBusy(true);
    setError(null);
    try {
      setPending(await clientApi.resetVerify(email.trim(), code.replace(/\s+/g, '')));
      setNewPassword('');
      setConfirmPassword('');
      setNotice(null);
      setStep('new-password');
    } catch (caught) {
      fail(caught, 'That code did not work.');
    } finally {
      setBusy(false);
    }
  }, [code, email, resetCodeReady]);

  const saveNewPassword = useCallback(async () => {
    if (pending === null || !newPasswordReady) return;
    setBusy(true);
    setError(null);
    try {
      clientSignedIn(await clientApi.setPassword(pending, newPassword));
    } catch (caught) {
      fail(caught, 'We could not save that password.');
    } finally {
      setBusy(false);
    }
  }, [clientSignedIn, newPassword, newPasswordReady, pending]);

  const staffPassword = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // The session listener in `useAuth` takes it from here.
      await staffPasswordSignIn(email.trim(), password);
    } catch (caught) {
      fail(caught, 'We could not sign you in.');
      setBusy(false);
    }
  }, [email, password]);

  const emailReady = email.includes('@') && email.trim().length > 3;
  const passwordStep = step === 'password' || step === 'staff-password';

  return (
    <div className="auth">
      <aside className="auth-rail">
        <div className="auth-brand">
          <span className="auth-mark">
            <RelayMark />
          </span>
          Relay
        </div>

        <div className="auth-pitch">
          <h2>The client said something. Now it is a ticket.</h2>
          <p>
            Feedback captured from inside the application it is about, with everything a developer
            would otherwise have to go back and ask for.
          </p>
        </div>

        <ol className="auth-steps">
          {STEPS.map(([number, text]) => (
            <li key={number}>
              <span>{number}</span>
              <span>{text}</span>
            </li>
          ))}
        </ol>
      </aside>

      <main className="auth-main">
        <div className="auth-form">
          <div className="auth-mark-inline">
            <span className="auth-mark">
              <RelayMark />
            </span>
            Relay
          </div>

          {step === 'reset' ? (
            <>
              <h1>Reset your password</h1>
              <p className="auth-lede" aria-live="polite">
                If there is a Relay account for <strong>{email.trim()}</strong>, we have sent it a code. Enter it
                below.
              </p>
            </>
          ) : step === 'new-password' ? (
            <>
              <h1>Choose a new password</h1>
              <p className="auth-lede">Enter it twice. You will use it from now on, here and in the feedback widget.</p>
            </>
          ) : step === 'code' ? (
            <>
              <h1>Check your email</h1>
              <p className="auth-lede" aria-live="polite">
                We have sent a code to <strong>{codeFor}</strong>. Enter it to finish signing in. You only
                need to do this once.
              </p>
            </>
          ) : (
            <>
              <h1>Sign in to Relay</h1>
              <p className="auth-lede">
                {step === 'email'
                  ? 'For Relay staff, and for clients following up on something they reported.'
                  : step === 'staff-password'
                    ? 'Local development: sign in with the password on your local staff account.'
                    : 'Use the password you set up from the feedback button in your application.'}
              </p>
            </>
          )}

          {error !== null ? (
            <div className="error-banner" role="alert" style={{ marginBottom: 14 }}>
              <span>{error}</span>
            </div>
          ) : null}
          {notice !== null ? (
            <p className="field-hint" role="status">
              {notice}
            </p>
          ) : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (busy) return;
              if (step === 'email' && emailReady) void continueWithEmail();
              if (step === 'password' && password.length > 0) void clientSignIn();
              if (step === 'staff-password' && password.length > 0) void staffPassword();
              if (step === 'code') void verify();
              if (step === 'reset') void checkResetCode();
              if (step === 'new-password') void saveNewPassword();
            }}
          >
            {step !== 'code' && step !== 'reset' && step !== 'new-password' ? (
              <div className="field">
                <label className="field-label" htmlFor="signin-email">
                  Email address
                </label>
                <input
                  id="signin-email"
                  className="input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  maxLength={320}
                  readOnly={step !== 'email'}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
            ) : null}

            {passwordStep ? (
              <div className="field">
                <label className="field-label" htmlFor="signin-password">
                  Password
                </label>
                <input
                  id="signin-password"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  maxLength={200}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>
            ) : null}

            {step === 'code' ? (
              <div className="field">
                <label className="field-label" htmlFor="signin-code">
                  Code from the email
                </label>
                <input
                  id="signin-code"
                  className="input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  maxLength={14}
                  onChange={(event) => setCode(event.target.value)}
                />
                <p className="field-hint">It can take a minute to arrive. Check your junk folder too.</p>
              </div>
            ) : null}

            {step === 'reset' ? (
              <div className="field">
                <label className="field-label" htmlFor="signin-reset-code">
                  Code from the email
                </label>
                <input
                  id="signin-reset-code"
                  className="input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  maxLength={14}
                  onChange={(event) => setCode(event.target.value)}
                />
                <p className="field-hint">It can take a minute to arrive. Check your junk folder too.</p>
              </div>
            ) : null}

            {step === 'new-password' ? (
              <>
                <div className="field">
                  <label className="field-label" htmlFor="signin-new-password">
                    New password
                  </label>
                  <input
                    id="signin-new-password"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    maxLength={200}
                    aria-describedby="signin-new-password-hint"
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <p className="field-hint" id="signin-new-password-hint">
                    At least {MIN_PASSWORD} characters.
                  </p>
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="signin-confirm-password">
                    New password again
                  </label>
                  <input
                    id="signin-confirm-password"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    maxLength={200}
                    aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                    aria-describedby="signin-confirm-password-hint"
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                  {confirmPassword.length > 0 && !passwordsMatch ? (
                    <p className="field-hint" id="signin-confirm-password-hint" style={{ color: 'var(--danger)' }}>
                      The two passwords do not match.
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}

            <button
              type="submit"
              className="button button-primary"
              style={{ width: '100%', marginTop: 6 }}
              disabled={
                busy ||
                (step === 'email' && !emailReady) ||
                (passwordStep && password.length === 0) ||
                (step === 'code' && !codeReady) ||
                (step === 'reset' && !resetCodeReady) ||
                (step === 'new-password' && !newPasswordReady)
              }
            >
              {busy ? <span className="spinner" aria-hidden="true" /> : null}
              {passwordStep ? 'Sign in' : step === 'new-password' ? 'Save password' : 'Continue'}
            </button>
          </form>

          {step !== 'email' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
              {step === 'password' ? (
                <button type="button" className="button button-ghost" disabled={busy} onClick={() => void startReset()}>
                  Forgot your password?
                </button>
              ) : step === 'reset' ? (
                <button
                  type="button"
                  className="button button-ghost"
                  disabled={busy}
                  onClick={() => {
                    void startReset().then(() => setNotice('A new code is on its way.'));
                  }}
                >
                  Send a new code
                </button>
              ) : step === 'code' ? (
                <button
                  type="button"
                  className="button button-ghost"
                  disabled={busy}
                  onClick={() => {
                    // Signing in again is what sends a code: there is no way to
                    // ask for one on the strength of an address alone.
                    void clientSignIn().then((sent) => {
                      if (sent) setNotice('A new code is on its way.');
                    });
                  }}
                >
                  Send a new code
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="button button-ghost" onClick={startOver}>
                Use a different email
              </button>
            </div>
          ) : null}

          {step === 'password' ? (
            <p className="auth-note">
              <strong>First time here?</strong> You need an account first. Open the feedback button inside the
              application you use, choose <em>Create an account</em>, then come back here with the same email
              and password.
            </p>
          ) : null}

          {staff !== null && staff.access !== 'unconfigured' && step !== 'staff-password' ? (
            <div className="auth-footer">
              <p style={{ margin: '0 0 10px' }}>Relay staff can also go straight to Zero Trust.</p>
              <button type="button" className="auth-sso" onClick={goToAccess} disabled={busy}>
                <CloudflareMark />
                Continue with Cloudflare Zero Trust
              </button>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/**
 * The landing page for the trip back from Access.
 *
 * Kept as its own route because a redirect target that renders the sign-in
 * page for the half-second before the token is exchanged looks exactly like a
 * failed sign-in, and people click the button again.
 */
export function AuthCallback(): JSX.Element {
  return (
    <div className="signin">
      <div className="signin-card">
        <h1>Signing you in…</h1>
        <p>
          <span className="spinner" aria-hidden="true" /> One moment.
        </p>
      </div>
    </div>
  );
}
